import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sunrise,
  Sun,
  Moon,
  Coffee,
  X,
  CheckCircle2,
  Save,
  Send,
  Trophy,
  Utensils
} from 'lucide-react';
import { getAiFeatureQuotas, submitDailyMeals } from '../api/dataService';
import { detectBrowserTimezone } from '../utils/timezone';
import { getQuestScheduleDateKey } from '../utils/questScheduleDate';
import {
  AI_FEATURE_KEYS,
  getFeatureQuota,
  getQuotaMessage,
  getRewardedUnlock,
  isQuotaBlocked,
} from '../utils/aiFeatureQuotas';
import { buildRewardedUnlockCopy, REWARDED_UNLOCK_CONTEXTS } from '../utils/rewardedAds';
import { useLocale } from '../context/LocaleContext';

const MEAL_KEYS = ['breakfast', 'lunch', 'dinner', 'snack'];
const REQUIRED = new Set(['breakfast', 'lunch', 'dinner']);
const MAX_LEN = 1500;

const MEAL_ICONS = {
  breakfast: Sunrise,
  lunch: Sun,
  dinner: Moon,
  snack: Coffee
};

const initialState = {
  breakfast: '',
  lunch: '',
  dinner: '',
  snack: ''
};

// --- Helper Functions ---

export function getLocalDayKey(dateValue, timezone) {
  const date = dateValue ? new Date(dateValue) : new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date);
    const acc = { year: '1970', month: '01', day: '01' };
    for (const p of parts) {
      if (p.type === 'year') acc.year = p.value;
      if (p.type === 'month') acc.month = p.value;
      if (p.type === 'day') acc.day = p.value;
    }
    return `${acc.year}-${acc.month}-${acc.day}`;
  } catch (_) {
    return '1970-01-01';
  }
}

const formatNumber = (num) => Math.round(num || 0).toLocaleString();

// --- Sub-Components ---

const XPCounter = ({ value }) => {
  const { t } = useLocale();
  const [count, setCount] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => setCount(value), 300);
    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <div className="flex flex-col items-center justify-center p-6 bg-yellow-500/10 rounded-2xl border border-yellow-500/30 shadow-[0_0_30px_rgba(234,179,8,0.1)]">
      <motion.span
        className="text-5xl font-black text-yellow-400 drop-shadow-md tabular-nums"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, type: 'spring' }}
      >
        +{formatNumber(count)} XP
      </motion.span>
      <span className="text-sm font-semibold text-yellow-200/60 uppercase tracking-widest mt-2">{t('dailyMealCheckin.earnedToday', 'Earned Today')}</span>
    </div>
  );
};

const NutritionCard = ({ estimated, expected, deviation }) => {
  const { t } = useLocale();
  const accuracy = Math.max(0, 100 - (deviation * 100));
  const getProgressColor = (val, max) => {
    const pct = (val / max) * 100;
    if (pct > 120 || pct < 80) return 'bg-amber-500';
    return 'bg-green-500';
  };

  const renderRow = (label, est, target, unit) => {
    const safeTarget = target || 1;
    const pct = Math.min((est / safeTarget) * 100, 100);

    return (
      <div className="space-y-1">
        <div className="flex justify-between text-xs font-medium">
          <span className="text-gray-400">{label}</span>
          <span className="text-gray-300">
            <span className="text-white">{formatNumber(est)}</span>
            <span className="mx-1 text-gray-500">/</span>
            {formatNumber(target)}{unit}
          </span>
        </div>
        <div className="h-1.5 w-full bg-gray-700/50 rounded-full overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${getProgressColor(est, safeTarget)}`}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="bg-[#1f2126] border border-white/5 rounded-xl p-4 space-y-4">
      <div className="flex items-center justify-between pb-2 border-b border-white/5">
        <h4 className="text-sm font-bold text-white flex items-center gap-2">
          <Utensils size={14} className="text-cyan-400" />
          {t('dailyMealCheckin.aiAnalysis', 'AI Analysis')}
        </h4>
        <div className="px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-[10px] font-bold text-blue-300">
          {t('dailyMealCheckin.matchScore', '{value}% MATCH', { value: Math.round(accuracy) })}
        </div>
      </div>

      <div className="space-y-3">
        {renderRow(t('dailyMealCheckin.calories', 'Calories'), estimated?.calories, expected?.calories, 'kcal')}
        <div className="grid grid-cols-3 gap-3 pt-1">
          {renderRow(t('dailyMealCheckin.protein', 'Protein'), estimated?.protein, expected?.protein, 'g')}
          {renderRow(t('dailyMealCheckin.carbs', 'Carbs'), estimated?.carbs, expected?.carbs, 'g')}
          {renderRow(t('dailyMealCheckin.fat', 'Sat. Fat'), estimated?.fat, expected?.fat, 'g')}
        </div>
      </div>

      {estimated?.reasoning && (
        <div className="text-[10px] text-gray-500 italic bg-black/20 p-2 rounded border border-white/5">
          "{estimated.reasoning}"
        </div>
      )}
    </div>
  );
};

// --- Main Form Component ---

function DailyMealCheckinModal({ isOpen, quest, allQuests, onClose, onSuccess, onSent }) {
  const { t } = useLocale();
  const timezone = useMemo(() => detectBrowserTimezone() || 'UTC', []);
  const scheduleTimezone = quest?.userTimezoneAtScheduling || timezone;
  const dayKey = useMemo(() => getQuestScheduleDateKey(quest) || getLocalDayKey(quest?.start || quest?.date, timezone), [quest, timezone]);
  const userId = quest?.userId || quest?.user?.id || 'user';
  const storageKey = `meal-day-draft:${userId}:${dayKey}`;

  const [entries, setEntries] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [sentState, setSentState] = useState({ sent: false, entries: initialState });
  const [mealAssignments, setMealAssignments] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [aiFeatureQuotas, setAiFeatureQuotas] = useState(null);
  const [isAiFeatureQuotasLoading, setIsAiFeatureQuotasLoading] = useState(false);
  const mealLabels = useMemo(() => ({
    breakfast: t('dailyMealCheckin.meals.breakfast', 'Breakfast'),
    lunch: t('dailyMealCheckin.meals.lunch', 'Lunch'),
    dinner: t('dailyMealCheckin.meals.dinner', 'Dinner'),
    snack: t('dailyMealCheckin.meals.snack', 'Snack / Other'),
  }), [t]);

  const mealQuestsForDay = useMemo(() => {
    if (!Array.isArray(allQuests)) return [quest].filter(Boolean);
    return allQuests.filter((q) => {
      if (!q || q.type !== 'Meal') return false;
      return getQuestScheduleDateKey(q) === dayKey || getLocalDayKey(q.start || q.date, timezone) === dayKey;
    });
  }, [allQuests, quest, dayKey, timezone]);

  const refreshAiFeatureQuotas = useCallback(() => {
    if (!isOpen) {
      return Promise.resolve();
    }

    setIsAiFeatureQuotasLoading(true);
    return getAiFeatureQuotas({ features: [AI_FEATURE_KEYS.MEAL_DAY_ESTIMATOR], force: true })
      .then((response) => {
        setAiFeatureQuotas(response.data || null);
      })
      .catch((err) => {
        console.warn('[DailyMealCheckinModal] Failed to refresh AI feature quotas', err);
      })
      .finally(() => {
        setIsAiFeatureQuotasLoading(false);
      });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      // Reset non-persistent state on close
      setShowResults(false);
      setResultData(null);
      setFeedback({ type: '', message: '' });
      return;
    }

    const saved = (() => {
      try {
        const raw = localStorage.getItem(storageKey);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    })();

    if (saved?.entries) {
      setEntries({ ...initialState, ...saved.entries });
    } else {
      setEntries(initialState);
    }
    setSentState({ sent: Boolean(saved?.sent), entries: saved?.entries || initialState });
  }, [isOpen, storageKey]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    setIsAiFeatureQuotasLoading(true);

    getAiFeatureQuotas({ features: [AI_FEATURE_KEYS.MEAL_DAY_ESTIMATOR] })
      .then((response) => {
        if (!cancelled) {
          setAiFeatureQuotas(response.data || null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('[DailyMealCheckinModal] Failed to fetch AI feature quotas', err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsAiFeatureQuotasLoading(false);
        }
      });

    const handleRefresh = () => {
      refreshAiFeatureQuotas();
    };

    window.addEventListener('forza:aiFeatureQuotasRefresh', handleRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener('forza:aiFeatureQuotasRefresh', handleRefresh);
    };
  }, [isOpen, refreshAiFeatureQuotas]);

  useEffect(() => {
    if (!isOpen) return;
    const byMeal = {};
    const used = new Set();

    // Prefer explicit mealType
    for (const q of mealQuestsForDay) {
      const slot = (q.mealType || '').toLowerCase();
      if (MEAL_KEYS.includes(slot) && !byMeal[slot]) {
        byMeal[slot] = q;
        used.add(q._id || q.id);
      }
    }

    // Fallback assign remaining
    for (const q of mealQuestsForDay) {
      const id = q._id || q.id;
      if (used.has(id)) continue;
      const empty = MEAL_KEYS.find((k) => !byMeal[k]);
      if (empty) {
        byMeal[empty] = q;
        used.add(id);
      }
    }

    setMealAssignments(byMeal);
  }, [isOpen, mealQuestsForDay]);

  const completedMealIds = MEAL_KEYS.reduce((acc, key) => {
    const q = mealAssignments[key];
    if (q?.completed) acc.push(q._id || q.id);
    return acc;
  }, []);

  const questIdsByMeal = MEAL_KEYS.reduce((acc, key) => {
    const q = mealAssignments[key];
    if (q?._id || q?.id) {
      acc[key] = q._id || q.id;
    }
    return acc;
  }, {});

  const isMealCompleted = (key) => {
    const q = mealAssignments[key];
    return Boolean(q?.completed);
  };

  const mealDayQuota = getFeatureQuota(aiFeatureQuotas, AI_FEATURE_KEYS.MEAL_DAY_ESTIMATOR);
  const mealDayQuotaBlocked = isQuotaBlocked(mealDayQuota);
  const mealDayQuotaMessage = getQuotaMessage(mealDayQuota);
  const mealDayRewardedUnlock = getRewardedUnlock(mealDayQuota);
  const canStartMealDayRewardedUnlock = Boolean(mealDayQuotaBlocked && mealDayRewardedUnlock?.canStart);
  const mealDayUnlockCopy = buildRewardedUnlockCopy(REWARDED_UNLOCK_CONTEXTS.MEAL_DAY, mealDayRewardedUnlock, t);
  const mealDayQuotaPending = isAiFeatureQuotasLoading && !aiFeatureQuotas;

  const handleChange = (key, value) => {
    setEntries((prev) => ({ ...prev, [key]: value.slice(0, MAX_LEN) }));
  };

  const handleMealDayRewardedUnlock = () => {
    const showRewardedAd = window?.forzaNative?.showRewardedAd;
    if (typeof showRewardedAd !== 'function') {
      setFeedback({
        type: 'info',
        message: mealDayUnlockCopy.fallbackMessage
      });
      return;
    }
    showRewardedAd('mealDayExtraUse');
  };

  // Logic Change: No validation for draft
  const handleSaveDraft = () => {
    localStorage.setItem(storageKey, JSON.stringify({ entries, sent: sentState.sent }));
    setFeedback({ type: 'info', message: t('dailyMealCheckin.draftSaved', 'Draft saved locally.') });
    // Auto-clear success message after 2s
    setTimeout(() => setFeedback(prev => prev.type === 'info' ? { type: '', message: '' } : prev), 2000);
  };

  const cleanedEntries = useMemo(() => {
    const result = {};
    for (const key of MEAL_KEYS) {
      const val = entries[key];
      if (typeof val === 'string' && val.trim()) result[key] = val.trim();
    }
    return result;
  }, [entries]);

  const validateClient = () => {
    for (const key of MEAL_KEYS) {
      const val = entries[key];
      if (val && val.length > MAX_LEN) {
        throw new Error(t('dailyMealCheckin.tooLong', '{meal} exceeds {max} characters.', {
          meal: mealLabels[key],
          max: MAX_LEN,
        }));
      }
    }
    for (const req of REQUIRED) {
      if (!mealAssignments[req]) continue;
      if (isMealCompleted(req)) continue;
      if (!cleanedEntries[req]) {
        throw new Error(t('dailyMealCheckin.requiredUnlessCompleted', '{meal} is required unless already completed.', {
          meal: mealLabels[req]
        }));
      }
    }
  };

  const handleSubmit = async () => {
    if (mealDayQuotaPending) {
      setFeedback({ type: 'info', message: t('dailyMealCheckin.checkingAvailability', 'Checking daily meal check-in availability...') });
      return;
    }

    if (mealDayQuotaBlocked) {
      setFeedback({ type: 'info', message: mealDayQuotaMessage || t('dailyMealCheckin.unavailable', 'Daily meal check-in unavailable right now.') });
      return;
    }

    try {
      validateClient();
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ type: 'info', message: t('dailyMealCheckin.analyzingMeals', 'Analyzing meals...') });

    try {
      const payload = {
        dateISO: dayKey,
        timezone: scheduleTimezone,
        entries: cleanedEntries,
        completedMealIds,
        questIdsByMeal
      };
      const response = await submitDailyMeals(payload);
      const data = response.data;

      // Mark as sent immediately
      const finalsEntries = Object.keys(cleanedEntries).length ? cleanedEntries : entries;
      localStorage.setItem(storageKey, JSON.stringify({ entries: finalsEntries, sent: true }));
      setSentState({ sent: true, entries: finalsEntries });
      window.dispatchEvent(new CustomEvent('forza:aiFeatureQuotasRefresh'));
      void refreshAiFeatureQuotas();

      // Instead of closing, show Results View
      setResultData(data);
      setShowResults(true);

      onSuccess?.(data);
      onSent?.();

    } catch (err) {
      const status = err.response?.status;
      const blockedQuota = err.response?.data?.quota || null;
      const message = err.response?.data?.message || t('dailyMealCheckin.submitFailed', 'Failed to submit. Please try again.');
      if (status === 409) {
        setSentState((prev) => ({ ...prev, sent: true }));
        localStorage.setItem(storageKey, JSON.stringify({ entries, sent: true }));
        onSent?.();
      }
      if (status === 429 && blockedQuota?.featureKey === AI_FEATURE_KEYS.MEAL_DAY_ESTIMATOR) {
        window.dispatchEvent(new CustomEvent('forza:aiFeatureQuotasRefresh'));
        void refreshAiFeatureQuotas();
      }
      setFeedback({ type: status === 409 || status === 429 ? 'info' : 'error', message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (key) => {
    const disabled = sentState.sent || isMealCompleted(key);
    const required = REQUIRED.has(key) && mealAssignments[key] && !isMealCompleted(key);
    const Icon = MEAL_ICONS[key];
    const label = mealLabels[key];

    return (
      <motion.div
        key={key}
        className={`group relative rounded-xl border transition-all duration-200 ${disabled ? 'bg-white/5 border-white/5 opacity-60' : 'bg-[#1f2126] border-white/10 hover:border-white/20 focus-within:border-cyan-500/50 focus-within:bg-[#23262b] focus-within:shadow-[0_0_20px_rgba(6,182,212,0.1)]'
          }`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-start p-4 gap-4">
          <div className={`p-2 rounded-lg ${disabled ? 'bg-white/5 text-gray-500' : 'bg-cyan-500/10 text-cyan-400'}`}>
            <Icon size={20} />
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-bold text-gray-200 block">
                {label} {required && <span className="text-red-400">*</span>}
              </label>
              {disabled && (
                <span className="flex items-center gap-1 text-[10px] uppercase font-bold text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">
                  <CheckCircle2 size={10} /> {t('dailyMealCheckin.completed', 'Completed')}
                </span>
              )}
            </div>

            <textarea
              value={entries[key]}
              onChange={(e) => handleChange(key, e.target.value)}
              disabled={disabled}
              className="w-full bg-transparent border-none p-0 text-sm text-white placeholder-gray-600 focus:ring-0 resize-none leading-relaxed"
              rows={2}
              maxLength={MAX_LEN}
              placeholder={t('dailyMealCheckin.placeholder', 'What did you have for {meal}?', {
                meal: label.toLowerCase()
              })}
            />

            <div className="text-[10px] text-gray-600 font-medium text-right transition-opacity opacity-0 group-hover:opacity-100 group-focus-within:opacity-100">
              {entries[key].length}/{MAX_LEN}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-200">
      <div className="w-full max-w-xl bg-[#181a1d] rounded-2xl shadow-2xl border border-white/10 overflow-hidden flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/5 bg-gradient-to-r from-cyan-900/20 to-transparent">
          <div>
            <h2 className="text-xl font-bold text-white tracking-tight">
              {showResults
                ? t('dailyMealCheckin.dailySummary', 'Daily Summary')
                : t('dailyMealCheckin.title', 'Daily Meal Check-in')}
            </h2>
            <p className="text-xs text-cyan-200/60 font-medium uppercase tracking-wider mt-1">
              {new Date(dayKey).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          <AnimatePresence mode="wait">

            {/* View: RESULTS */}
            {showResults && resultData ? (
              <motion.div
                key="results"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="space-y-6"
              >
                {/* Level Up Banner */}
                {resultData.leveledUp && (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: [1, 1.1, 1], opacity: 1 }}
                    transition={{ duration: 0.6, repeat: Infinity, repeatType: 'reverse', repeatDelay: 2 }}
                    className="bg-gradient-to-r from-yellow-600/20 via-yellow-500/20 to-yellow-600/20 border border-yellow-500/40 p-4 rounded-xl text-center"
                  >
                    <Trophy className="mx-auto text-yellow-400 mb-2" size={32} />
                    <h3 className="text-2xl font-black text-yellow-400 uppercase tracking-widest">{t('dailyMealCheckin.levelUp', 'Level Up!')}</h3>
                    <p className="text-yellow-200 text-sm font-bold">{t('dailyMealCheckin.welcomeLevel', 'Welcome to Level {level}', { level: resultData.newLevel })}</p>
                  </motion.div>
                )}

                <XPCounter value={resultData.xpGained} />

                <NutritionCard
                  estimated={resultData.estimatedTotals}
                  expected={resultData.expectedTotals}
                  deviation={resultData.deviation}
                />

                <div className="text-center text-xs text-gray-500">
                  <p>{resultData.message}</p>
                </div>
              </motion.div>
            ) : (

              /* View: FORM */
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                {renderField('breakfast')}
                {renderField('lunch')}
                {renderField('dinner')}
                {renderField('snack')}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/5 bg-[#181a1d]">
          {!showResults && mealDayQuotaBlocked && (
            <div className="mb-4 rounded-lg border border-amber-400/40 bg-amber-900/30 px-4 py-3 text-sm text-amber-100">
              <p>{canStartMealDayRewardedUnlock ? mealDayUnlockCopy.headline : mealDayQuotaMessage}</p>
              {canStartMealDayRewardedUnlock && (
                <button
                  type="button"
                  onClick={handleMealDayRewardedUnlock}
                  className="mt-3 inline-flex items-center justify-center rounded-lg border border-amber-300/50 bg-amber-300/10 px-3 py-2 text-xs font-semibold text-amber-100 transition-colors hover:border-amber-200 hover:bg-amber-300/20"
                >
                  {mealDayUnlockCopy.buttonLabel}
                </button>
              )}
            </div>
          )}

          {feedback.message && !showResults && (
            <div className={`mb-4 text-xs font-bold text-center px-4 py-2 rounded-lg ${feedback.type === 'error' ? 'bg-red-500/10 text-red-400' : 'bg-blue-500/10 text-blue-400'
              }`}>
              {feedback.message}
            </div>
          )}

          {showResults ? (
            <button
              onClick={onClose}
              className="w-full py-4 rounded-xl bg-white text-black font-bold text-sm hover:bg-gray-200 transition shadow-[0_0_30px_rgba(255,255,255,0.1)]"
            >
              {t('dailyMealCheckin.continue', 'Continue')}
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveDraft}
                disabled={isSubmitting || sentState.sent}
                className="flex items-center gap-2 px-6 py-3 rounded-xl border border-white/10 text-gray-400 font-bold text-sm hover:bg-white/5 hover:text-white transition disabled:opacity-50"
              >
                <Save size={16} />
                <span className="hidden sm:inline">{t('dailyMealCheckin.saveDraft', 'Save Draft')}</span>
              </button>

              <button
                onClick={handleSubmit}
                disabled={isSubmitting || sentState.sent || mealDayQuotaBlocked || mealDayQuotaPending}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-500 transition shadow-lg shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                  >
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                  </motion.div>
                ) : (
                  <>
                    <Send size={16} />
                    {mealDayQuotaPending
                      ? t('dailyMealCheckin.checkingShort', 'Checking...')
                      : sentState.sent
                        ? t('dailyMealCheckin.alreadySent', 'Already Sent')
                        : t('dailyMealCheckin.sendDailyCheckin', 'Send Daily Check-in')}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

DailyMealCheckinModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  quest: PropTypes.object,
  allQuests: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  onSent: PropTypes.func
};

export default DailyMealCheckinModal;
